import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { LangProvider } from '@/context/LangContext';
import { WorkspaceProvider } from '@/context/WorkspaceContext';
import { initPlausible } from '@/lib/analytics';

initPlausible();
import ProtectedRoute from '@/components/ProtectedRoute';
import AppLayout from '@/components/AppLayout';
import Landing from '@/pages/Landing';
import SignIn from '@/pages/auth/SignIn';
import ResetPassword from '@/pages/auth/ResetPassword';
import ResetPasswordConfirm from '@/pages/auth/ResetPasswordConfirm';
import Onboarding from '@/pages/app/Onboarding';
import Dashboard from '@/pages/app/Dashboard';
import Projects from '@/pages/app/Projects';
import ProjectDetail from '@/pages/app/ProjectDetail';
import Formation from '@/pages/app/Formation';
import FormationUseCase from '@/pages/app/FormationUseCase';
import CampusPillar from '@/pages/app/CampusPillar';
import CampusLesson from '@/pages/app/CampusLesson';
import Audit from '@/pages/app/Audit';
import Admin from '@/pages/app/Admin';
import AIPage from '@/pages/app/AIPage';
import Grants from '@/pages/app/Grants';
import Courriers from '@/pages/app/Courriers';
import GedGlobal from '@/pages/app/GedGlobal';
import Approvals from '@/pages/app/Approvals';
import Templates from '@/pages/app/Templates';
import Marketplace from '@/pages/app/Marketplace';
import Orders from '@/pages/app/Orders';
import Branding from '@/pages/app/Branding';
import SuperAdmin from '@/pages/app/SuperAdmin';
import SuperAdminLayout from '@/components/SuperAdminLayout';
import Team from '@/pages/app/Team';
import Profile from '@/pages/app/Profile';
import Notifications from '@/pages/app/Notifications';
import MonAccompagnement from '@/pages/app/MonAccompagnement';
import InviteAccept from '@/pages/auth/InviteAccept';
import AccessRequest from '@/pages/auth/AccessRequest';
import SignUpGate from '@/pages/auth/SignUpGate';
import BillingSuccess from '@/pages/BillingSuccess';
import BillingCancel from '@/pages/BillingCancel';
import Donate from '@/pages/Donate';
import PublicMeeting from '@/pages/PublicMeeting';
import CmsPage from '@/pages/CmsPage';
import Options from '@/pages/Options';
import Consortium from '@/pages/Consortium';
import Partenaires from '@/pages/Partenaires';
import SignupFree from '@/pages/SignupFree';
import Sectors from '@/pages/Sectors';
import ApiIntegrations from '@/pages/ApiIntegrations';
import Diagnostic from '@/pages/Diagnostic';
import ProjectScore from '@/pages/ProjectScore';
import Tarifs from '@/pages/Tarifs';
import ProjectKiosk from '@/pages/app/ProjectKiosk';
import Guide from '@/pages/app/Guide';
import VerifyCertificate from '@/pages/VerifyCertificate';
import CampusPublic from '@/pages/CampusPublic';
import AuthCallback from '@/pages/auth/AuthCallback';
import { MaintenanceGate } from '@/pages/Maintenance';
import AdrnsLayout from '@/components/adrns/AdrnsLayout';
import AdrnsDashboard from '@/pages/adrns/AdrnsDashboard';
import AdrnsDossiers from '@/pages/adrns/AdrnsDossiers';
import AdrnsDossierDetail from '@/pages/adrns/AdrnsDossierDetail';
import AdrnsModulePage from '@/pages/adrns/AdrnsModulePage';
import AdrnsMembres from '@/pages/adrns/AdrnsMembres';
import AdrnsMissions from '@/pages/adrns/AdrnsMissions';
import AdrnsMissionDetail from '@/pages/adrns/AdrnsMissionDetail';

export default function App() {
    return (
        <LangProvider>
            <AuthProvider>
                <WorkspaceProvider>
                    <BrowserRouter>
                        <MaintenanceGate>
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/donate" element={<Donate />} />
                            <Route path="/apply" element={<AccessRequest />} />
                            <Route path="/billing/success" element={<BillingSuccess />} />
                            <Route path="/billing/cancel" element={<BillingCancel />} />
                            <Route path="/p/options" element={<Options />} />
                            <Route path="/p/consortium" element={<Consortium />} />
                            <Route path="/p/partenaires" element={<Partenaires />} />
                            <Route path="/signup-free" element={<SignupFree />} />
                            <Route path="/p/secteurs" element={<Sectors />} />
                            <Route path="/p/api" element={<ApiIntegrations />} />
                            <Route path="/p/diagnostic" element={<Diagnostic />} />
                            <Route path="/p/project-score" element={<ProjectScore />} />
                            <Route path="/p/tarifs" element={<Tarifs />} />
                            <Route path="/p/campus" element={<CampusPublic />} />
                            <Route path="/p/verify-certificate" element={<VerifyCertificate />} />
                            <Route path="/p/:slug" element={<CmsPage />} />
                            <Route path="/meeting/:token" element={<PublicMeeting />} />
                            <Route path="/auth/signin" element={<SignIn />} />
                            <Route path="/auth/reset" element={<ResetPassword />} />
                            <Route path="/auth/reset/confirm" element={<ResetPasswordConfirm />} />
                            <Route path="/auth/signup" element={<SignUpGate />} />
                            <Route path="/auth/callback" element={<AuthCallback />} />
                            <Route path="/auth/invite/:token" element={<InviteAccept />} />
                            <Route path="/app/projects/:id/kiosk" element={<ProtectedRoute><ProjectKiosk /></ProtectedRoute>} />
                            <Route path="/app/adrns" element={<ProtectedRoute><AdrnsLayout /></ProtectedRoute>}>
                                <Route index element={<AdrnsDashboard />} />
                                <Route path="dossiers" element={<AdrnsDossiers />} />
                                <Route path="dossiers/:id" element={<AdrnsDossierDetail />} />
                                <Route path="dossiers/:id/module/:mid" element={<AdrnsModulePage />} />
                                <Route path="missions" element={<AdrnsMissions />} />
                                <Route path="dossiers/:id/missions/:mid" element={<AdrnsMissionDetail />} />
                                <Route path="membres" element={<AdrnsMembres />} />
                            </Route>
                            <Route path="/app/super-admin" element={<ProtectedRoute><SuperAdminLayout /></ProtectedRoute>}>
                                <Route index element={<SuperAdmin />} />
                                <Route path=":section" element={<SuperAdmin />} />
                            </Route>
                            <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="onboarding" element={<Onboarding />} />
                                <Route path="dashboard" element={<Dashboard />} />
                                <Route path="projects" element={<Projects />} />
                                <Route path="projects/:id" element={<ProjectDetail />} />
                                <Route path="grants" element={<Grants />} />
                                <Route path="courriers" element={<Courriers />} />
                                <Route path="ged" element={<GedGlobal />} />
                                <Route path="approvals" element={<Approvals />} />
                                <Route path="templates" element={<Templates />} />
                                <Route path="marketplace" element={<Marketplace />} />
                                <Route path="orders" element={<Orders />} />
                                <Route path="branding" element={<Branding />} />
                                <Route path="formation" element={<Formation />} />
                                <Route path="formation/pilier/:slug" element={<CampusPillar />} />
                                <Route path="formation/lecon/:id" element={<CampusLesson />} />
                                <Route path="formation/:slug" element={<FormationUseCase />} />
                                <Route path="ai" element={<AIPage />} />
                                <Route path="audit" element={<Audit />} />
                                <Route path="guide" element={<Guide />} />
                                <Route path="admin" element={<Admin />} />
                                <Route path="team" element={<Team />} />
                                <Route path="profile" element={<Profile />} />
                                <Route path="notifications" element={<Notifications />} />
                                <Route path="mon-accompagnement" element={<MonAccompagnement />} />
                            </Route>
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        </MaintenanceGate>
                        <Toaster position="top-right" />
                    </BrowserRouter>
                </WorkspaceProvider>
            </AuthProvider>
        </LangProvider>
    );
}
